import React, { useState } from 'react';

// Position is stored as `{vertical}-{horizontal}` by the position-grid component (e.g. 'top-center',
// 'center-center', 'bottom-right'); older objects used camelCase ('topCenter'). Accept both and map
// each axis onto flexbox alignment.
const VERTICAL_TO_FLEX = { top: 'flex-start', center: 'center', middle: 'center', bottom: 'flex-end' };
const HORIZONTAL_TO_FLEX = { left: 'flex-start', center: 'center', right: 'flex-end' };

const parsePosition = (position) => {
  const raw = position || 'top-center';
  if (raw.includes('-')) {
    const [vertical, horizontal] = raw.split('-');
    return { vertical, horizontal };
  }
  const match = raw.match(/^(top|middle|center|bottom)(left|center|right)$/i);
  return match
    ? { vertical: match[1].toLowerCase(), horizontal: match[2].toLowerCase() }
    : { vertical: 'top', horizontal: 'center' };
};

const resolveImagePosition = (imagePosition) => {
  const { vertical, horizontal } = parsePosition(imagePosition);
  return {
    vertical: VERTICAL_TO_FLEX[vertical] ?? 'flex-start',
    horizontal: HORIZONTAL_TO_FLEX[horizontal] ?? 'center',
  };
};

const getObjectPosition = (resolvedImagePosition) => {
  let verticalPos = 'center';
  let horizontalPos = 'center';

  if (resolvedImagePosition?.vertical === 'flex-start') {
    verticalPos = 'top';
  } else if (resolvedImagePosition?.vertical === 'flex-end') {
    verticalPos = 'bottom';
  }

  if (resolvedImagePosition?.horizontal === 'flex-start') {
    horizontalPos = 'left';
  } else if (resolvedImagePosition?.horizontal === 'flex-end') {
    horizontalPos = 'right';
  }

  return `${horizontalPos} ${verticalPos}`;
};

const getObjectFit = (imageSize) => {
  switch (imageSize) {
    case 'alwaysFit':
      return 'contain';
    case 'stretch':
      return 'fill';
    case 'alwaysFill':
      return 'cover';
    default:
      return undefined;
  }
};

// Validate the image src before putting it in the DOM. Only http(s) URLs (including relative ones,
// which resolve against the page origin) and inline image data URIs are allowed; anything else
// (e.g. javascript: or data:text/html) is rejected.
const isSafeImageSrc = (src) => {
  if (typeof src !== 'string' || !src.trim()) {
    return false;
  }
  try {
    const url = new URL(src, window.location.origin);
    if (url.protocol === 'https:' || url.protocol === 'http:') {
      return true;
    }
    return url.protocol === 'data:' && /^data:image\//i.test(src.trim());
  } catch {
    return false;
  }
};

function Image({
  representation,
  src,
  label,
  title,
  subtitle,
  cellBgColor,
  borderColor: borderColorProp,
  placeholderBackground,
  textStyles,
  selected = false,
  selectionColor = '#009845',
  opacity = 1,
}) {
  const {
    imageSize = 'alwaysFit',
    imagePosition,
    titlePosition = 'top-center',
    textOverlay = true,
    titleBackground = true,
    cornerRadius = 4,
    borderWidth = 0,
    borderColor: representationBorderColor,
  } = representation;
  const isFitHeight = imageSize === 'fitHeight';
  const isFitWidth = imageSize === 'fitWidth';
  const resolvedImagePosition = resolveImagePosition(imagePosition);
  const safeSrc = isSafeImageSrc(src) ? src : null;
  // Track the src that failed to load (rather than a plain boolean) so a new src coming in on the
  // same cell instance (e.g. on scroll, since react-window recycles cells) gets a fresh attempt
  // instead of staying stuck on a broken-image icon from whatever previously errored here.
  const [erroredSrc, setErroredSrc] = useState(null);
  const hasLoadError = safeSrc !== null && safeSrc === erroredSrc;
  const resolvedCornerRadius = `${cornerRadius}px`;
  const resolvedBorderColor =
    borderColorProp ??
    (typeof representationBorderColor === 'string' ? representationBorderColor : representationBorderColor?.color) ??
    '#d9d9d9';
  // Selected cells get a colored border; otherwise use the configured border
  let border;
  if (selected) {
    border = `2px solid ${selectionColor}`;
  } else if (borderWidth > 0) {
    border = `${borderWidth}px solid ${resolvedBorderColor}`;
  } else {
    border = '2px solid transparent';
  }

  const imgNode =
    safeSrc && !hasLoadError ? (
      <img
        src={safeSrc}
        alt={label}
        draggable={false}
        // A 404 or otherwise broken URL should degrade to the same placeholder shown for a missing
        // src, rather than the browser's native broken-image icon.
        onError={() => setErroredSrc(safeSrc)}
        style={{
          width: isFitHeight ? 'auto' : '100%',
          height: isFitWidth ? 'auto' : '100%',
          objectFit: getObjectFit(imageSize),
          objectPosition: getObjectPosition(resolvedImagePosition),
          userSelect: 'none',
          WebkitUserSelect: 'none',
        }}
      />
    ) : null;

  // The title is the dimension (cell) value, and the subtitle is a per-value expression. Both are
  // overlaid on top of the image and aligned per the title alignment settings.
  const resolvedTitleAlignment = resolveImagePosition(titlePosition);
  const hasOverlayText = textOverlay !== false && (title || subtitle);
  const text = textStyles ?? {};
  const hasImage = Boolean(safeSrc);
  const textShadow = hasImage && !titleBackground ? (text.shadowOnImage ?? 'none') : 'none';

  const overlayNode = hasOverlayText ? (
    <div
      data-key="image-title-overlay"
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        justifyContent: resolvedTitleAlignment.horizontal,
        alignItems: resolvedTitleAlignment.vertical,
        padding: '4px',
        pointerEvents: 'none',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          maxWidth: '100%',
          padding: titleBackground && hasImage ? '2px 6px' : 0,
          borderRadius: titleBackground && hasImage ? '3px' : 0,
          backgroundColor: titleBackground && hasImage ? (text.backdropColor ?? 'transparent') : 'transparent',
          overflow: 'hidden',
          textAlign: resolvedTitleAlignment.horizontal === 'center' ? 'center' : undefined,
        }}
      >
        {title && (
          <div
            data-key="image-title"
            style={{
              fontFamily: text.fontFamily,
              fontSize: text.fontSize ?? '13px',
              fontWeight: text.fontWeight ?? 'bold',
              fontStyle: text.fontStyle ?? 'normal',
              textDecoration: text.textDecoration ?? 'initial',
              color: text.color ?? '#333333',
              textShadow,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </div>
        )}
        {subtitle && (
          <div
            data-key="image-subtitle"
            style={{
              fontFamily: text.fontFamily,
              fontSize: text.subtitleFontSize ?? '11px',
              fontStyle: text.fontStyle ?? 'normal',
              color: text.color ?? '#333333',
              opacity: 0.8,
              textShadow,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {subtitle}
          </div>
        )}
      </div>
    </div>
  ) : null;

  return (
    <div
      data-key="image-horizontal-container"
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        justifyContent: resolvedImagePosition.horizontal,
        backgroundColor: cellBgColor || placeholderBackground || undefined,
        borderRadius: resolvedCornerRadius,
        border,
        opacity,
        boxSizing: 'border-box',
        alignItems: resolvedImagePosition.vertical,
      }}
    >
      {imgNode}
      {overlayNode}
    </div>
  );
}

export default Image;
